import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useQuotas, useUpsertQuota } from '../../hooks';
import { Save, X, Plus } from 'lucide-react';
import { useToast } from '../../components/ui/toast';

interface QuotaRow {
  id: string;
  emp_code: string;
  year: number;
  sick_quota: number;
  personal_quota: number;
  vacation_quota: number;
  carried_days: number;
  opening_sick: number;
  opening_personal: number;
  opening_vacation: number;
}

type FormData = {
  emp_code: string; year: number;
  sick_quota: number; personal_quota: number; vacation_quota: number;
  opening_sick: number; opening_personal: number; opening_vacation: number;
};

const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function Remaining({ entitled, opening }: { entitled: unknown; opening: unknown }) {
  const left = num(entitled) - num(opening);
  return (
    <span className={left < 0 ? 'text-red-600 font-medium' : 'text-gray-700'}>
      {left} วัน{left < 0 && ' (ติดลบ)'}
    </span>
  );
}

function EditModal({ quota, onClose }: { quota?: QuotaRow; onClose: () => void }) {
  const { register, handleSubmit, control } = useForm<FormData>({
    defaultValues: quota
      ? { ...quota }
      : {
          year: new Date().getFullYear(),
          sick_quota: 30, personal_quota: 3, vacation_quota: 6,
          opening_sick: 0, opening_personal: 0, opening_vacation: 0,
        },
  });
  const upsert = useUpsertQuota();
  const toast = useToast();
  const w = useWatch({ control });

  async function onSubmit(data: FormData) {
    try {
      await upsert.mutateAsync({
        ...data,
        year: Number(data.year),
        sick_quota: Number(data.sick_quota),
        personal_quota: Number(data.personal_quota),
        vacation_quota: Number(data.vacation_quota),
        opening_sick: Number(data.opening_sick),
        opening_personal: Number(data.opening_personal),
        opening_vacation: Number(data.opening_vacation),
      } as Record<string, unknown>);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold">{quota ? 'แก้ไข' : 'เพิ่ม'}สิทธิ์การลา</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <label className="label">รหัสพนักงาน *</label>
            <input {...register('emp_code', { required: true })} className="input" readOnly={!!quota} />
          </div>
          <div>
            <label className="label">ปี (YYYY) *</label>
            <input type="number" {...register('year', { required: true })} className="input" readOnly={!!quota} />
          </div>

          <div>
            <p className="text-xs font-medium text-gray-600 mb-1.5">สิทธิ์ทั้งปี (วัน)</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="label">ป่วย</label>
                <input type="number" step="0.5" {...register('sick_quota', { required: true, min: 0 })} className="input" />
              </div>
              <div>
                <label className="label">กิจส่วนตัว</label>
                <input type="number" step="0.5" {...register('personal_quota', { required: true, min: 0 })} className="input" />
              </div>
              <div>
                <label className="label">พักร้อน</label>
                <input type="number" step="0.5" {...register('vacation_quota', { required: true, min: 0 })} className="input" />
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-600 mb-1.5">ใช้ไปแล้วก่อนเริ่มใช้ระบบ (วัน)</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="label">ป่วย</label>
                <input type="number" step="0.5" {...register('opening_sick', { min: 0 })} className="input" />
              </div>
              <div>
                <label className="label">กิจส่วนตัว</label>
                <input type="number" step="0.5" {...register('opening_personal', { min: 0 })} className="input" />
              </div>
              <div>
                <label className="label">พักร้อน</label>
                <input type="number" step="0.5" {...register('opening_vacation', { min: 0 })} className="input" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              สำหรับพนักงานที่ลาไปแล้วก่อนบริษัทเริ่มใช้ระบบนี้ ระบบจะนับรวมกับใบลาที่ยื่นในระบบ
            </p>
          </div>

          <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-sm space-y-1">
            <p className="text-xs font-medium text-gray-600 mb-1">คงเหลือหลังบันทึก</p>
            <div className="flex justify-between"><span className="text-gray-500">ป่วย</span><Remaining entitled={w.sick_quota} opening={w.opening_sick} /></div>
            <div className="flex justify-between"><span className="text-gray-500">กิจส่วนตัว</span><Remaining entitled={w.personal_quota} opening={w.opening_personal} /></div>
            <div className="flex justify-between">
              <span className="text-gray-500">พักร้อน{quota?.carried_days ? ` (+ ยกยอด ${quota.carried_days})` : ''}</span>
              <Remaining entitled={num(w.vacation_quota) + (quota?.carried_days ?? 0)} opening={w.opening_vacation} />
            </div>
          </div>

          <button type="submit" disabled={upsert.isPending} className="btn-primary w-full">
            <Save className="w-4 h-4" /> {upsert.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
        </form>
      </div>
    </div>
  );
}

export function HRQuotas() {
  const { data, isLoading } = useQuotas() as { data: QuotaRow[] | undefined; isLoading: boolean };
  const [editing, setEditing] = useState<QuotaRow | null | 'new'>(null);
  const currentYear = new Date().getFullYear();
  const filtered = (data ?? []).filter((q) => q.year === currentYear);

  if (isLoading) return <AdminLayout title="สิทธิ์การลา"><p className="py-8 text-center text-gray-500">กำลังโหลด...</p></AdminLayout>;

  const cell = (entitled: number, opening: number) => (
    <>
      {entitled - opening}
      {opening > 0 && <span className="text-xs text-gray-400"> / {entitled}</span>}
    </>
  );

  return (
    <AdminLayout title="สิทธิ์การลา">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">ปี {currentYear}</p>
        <button onClick={() => setEditing('new')} className="btn-primary btn-slim"><Plus className="w-3.5 h-3.5" /> เพิ่ม</button>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-600">พนักงาน</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">ป่วย</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">กิจ</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">พักร้อน</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((q) => (
              <tr key={q.emp_code}>
                <td className="px-3 py-2 font-mono text-xs">{q.emp_code}</td>
                <td className="px-3 py-2 text-right">{cell(q.sick_quota, q.opening_sick)}</td>
                <td className="px-3 py-2 text-right">{cell(q.personal_quota, q.opening_personal)}</td>
                <td className="px-3 py-2 text-right">{cell(q.vacation_quota + q.carried_days, q.opening_vacation)}</td>
                <td className="px-2 py-2 text-right">
                  <button onClick={() => setEditing(q)} className="text-xs text-brand-600 underline">แก้ไข</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center text-gray-500 py-6">ยังไม่มีข้อมูล</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {filtered.some((q) => q.opening_sick || q.opening_personal || q.opening_vacation) && (
        <p className="text-xs text-gray-500 mt-2">ตัวเลขคือวันคงเหลือ / สิทธิ์เต็มปี (เมื่อมีวันที่ใช้ไปก่อนเริ่มระบบ)</p>
      )}
      {editing !== null && (
        <EditModal
          quota={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </AdminLayout>
  );
}
